import { ChevronDown, Inbox } from "lucide-react";
import { useState, type ReactNode } from "react";

import { AvatarPlaceholder } from "components/AvatarPlaceholder";
import { EmptyState } from "components/states";
import { Card, CardContent } from "components/ui/card";
import { APP_LOCALE } from "utils";

import { usableTimeZone } from "../../event-experience/agenda-schedule";
import { EventPersonSheet } from "../../event-experience/EventPersonSheet";

/**
 * What an attendee asked the organisers for, in the shape the Requests tab
 * lists. `type` says which kind: table switches (TNG-92) and dinner seat
 * changes (TNG-94), each with its own row builder and actions, in one list.
 */
export type AdminRequestType = "track-switch" | "seat-change";

/**
 * What each type is called on its card. The same words as the attendee's
 * cards and the notifications they get, so "table" alone never has to say
 * which of the two it means.
 */
export const REQUEST_TYPE_LABELS: Record<AdminRequestType, string> = {
  "track-switch": "Theme table switch",
  "seat-change": "Dinner seat change",
};

/** What the card shows; the profile sheet loads the rest from the id. */
export interface AdminRequestPerson {
  attendeeId: string;
  name: string;
  role?: string;
  company?: string;
  avatar?: string;
}

export interface AdminRequestRow {
  type: AdminRequestType;
  /** Unique within its type. */
  id: string;
  open: boolean;
  /** Null when the roster row is gone; `personFallback` names them then. */
  person: AdminRequestPerson | null;
  personFallback: string;
  /** What they have now, e.g. their current table. */
  current: { caption: string; label: string };
  /** What they asked for, with a detail such as how many sit there. */
  requested: { caption: string; label: string; detail?: string };
  reason?: string;
  requestedAt: Date | null;
  /** When a decided request closed, for ordering the decided list. */
  decidedAt?: Date | null;
  /** For a decided request: what happened and when, in words. */
  outcome?: string;
  outcomeTone?: "positive" | "negative" | "neutral";
}

/**
 * The organiser's list of requests: open ones first, each with its actions,
 * and the decided ones folded away underneath.
 */
export function RequestList({
  eventId,
  rows,
  timeZone,
  renderActions,
  emptyTitle,
  emptyText,
}: {
  eventId: string;
  rows: AdminRequestRow[];
  timeZone?: string;
  renderActions: (row: AdminRequestRow) => ReactNode;
  emptyTitle: string;
  emptyText: string;
}) {
  const [profileId, setProfileId] = useState<string | null>(null);
  const open = rows.filter((row) => row.open);
  const decided = rows.filter((row) => !row.open);

  return (
    <div className="space-y-6">
      {open.length === 0 ? (
        <EmptyState icon={Inbox} title={emptyTitle} testId="requests-empty">
          {emptyText}
        </EmptyState>
      ) : (
        <ul className="space-y-3" data-testid="requests-open">
          {open.map((row) => (
            <li key={`${row.type}:${row.id}`}>
              <RequestCard
                row={row}
                timeZone={timeZone}
                onOpenPerson={(person) => setProfileId(person.attendeeId)}
                actions={renderActions(row)}
              />
            </li>
          ))}
        </ul>
      )}

      {decided.length > 0 && (
        <details className="group" data-testid="requests-decided">
          <summary className="flex min-h-11 cursor-pointer list-none items-center gap-2 text-sm font-medium text-foreground [&::-webkit-details-marker]:hidden">
            <ChevronDown
              className="h-4 w-4 transition-transform group-open:rotate-180"
              aria-hidden
            />
            Decided ({decided.length})
          </summary>
          <ul className="mt-3 space-y-3">
            {decided.map((row) => (
              <li key={`${row.type}:${row.id}`}>
                <RequestCard
                  row={row}
                  timeZone={timeZone}
                  onOpenPerson={(person) => setProfileId(person.attendeeId)}
                />
              </li>
            ))}
          </ul>
        </details>
      )}

      {/* The organiser's variant: the attendee record, without the
          private note an attendee keeps about somebody. */}
      <EventPersonSheet
        eventId={eventId}
        variant="organiser"
        person={profileId ? { attendeeId: profileId } : null}
        onClose={() => setProfileId(null)}
      />
    </div>
  );
}

function RequestCard({
  row,
  timeZone,
  onOpenPerson,
  actions,
}: {
  row: AdminRequestRow;
  timeZone?: string;
  onOpenPerson: (person: AdminRequestPerson) => void;
  actions?: ReactNode;
}) {
  const name = row.person?.name ?? row.personFallback;
  return (
    <Card
      className="border-border shadow-none"
      data-testid={`request-${row.type}-${row.id}`}
    >
      <CardContent className="space-y-3 py-4">
        <p
          className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
          data-testid="request-kind"
        >
          {REQUEST_TYPE_LABELS[row.type]}
        </p>
        <div className="flex items-start gap-3">
          {row.person ? (
            <button
              type="button"
              onClick={() => row.person && onOpenPerson(row.person)}
              aria-label={`${name}, view profile`}
              className="flex min-h-11 min-w-0 flex-1 items-center gap-3 rounded-md text-left focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="request-person"
            >
              <Avatar person={row.person} />
              <PersonText name={name} person={row.person} />
            </button>
          ) : (
            <div className="flex min-h-11 min-w-0 flex-1 items-center gap-3">
              <AvatarPlaceholder name={name} size="sm" />
              <PersonText name={name} person={null} />
            </div>
          )}
          {row.requestedAt && (
            <time
              dateTime={row.requestedAt.toISOString()}
              className="shrink-0 pt-1 text-xs text-muted-foreground"
            >
              {formatRequestTime(row.requestedAt, timeZone)}
            </time>
          )}
        </div>

        <dl className="grid gap-x-4 gap-y-1 text-sm sm:grid-cols-2">
          <div className="min-w-0">
            <dt className="text-xs text-muted-foreground">
              {row.current.caption}
            </dt>
            <dd
              className="break-words text-foreground"
              data-testid="request-current"
            >
              {row.current.label}
            </dd>
          </div>
          <div className="min-w-0">
            <dt className="text-xs text-muted-foreground">
              {row.requested.caption}
            </dt>
            <dd
              className="break-words font-medium text-foreground"
              data-testid="request-requested"
            >
              {row.requested.label}
              {row.requested.detail && (
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  {row.requested.detail}
                </span>
              )}
            </dd>
          </div>
        </dl>

        {row.reason && (
          <blockquote
            className="whitespace-pre-line break-words border-l-2 border-border pl-3 text-sm text-muted-foreground"
            data-testid="request-reason"
          >
            {row.reason}
          </blockquote>
        )}

        {row.outcome && (
          <p
            className={
              row.outcomeTone === "positive"
                ? "text-sm font-medium text-foreground"
                : row.outcomeTone === "negative"
                  ? "text-sm font-medium text-destructive-text"
                  : "text-sm text-muted-foreground"
            }
            data-testid="request-outcome"
          >
            {row.outcome}
          </p>
        )}

        {actions}
      </CardContent>
    </Card>
  );
}

function Avatar({ person }: { person: AdminRequestPerson }) {
  return person.avatar ? (
    <img
      src={person.avatar}
      alt=""
      className="h-10 w-10 shrink-0 rounded-full object-cover"
    />
  ) : (
    <AvatarPlaceholder name={person.name} size="sm" />
  );
}

function PersonText({
  name,
  person,
}: {
  name: string;
  person: AdminRequestPerson | null;
}) {
  const detail = person
    ? [person.role, person.company].filter(Boolean).join(", ")
    : "No longer on the roster";
  return (
    <span className="min-w-0">
      <span className="block truncate font-medium text-foreground">{name}</span>
      {detail && (
        <span className="block truncate text-xs text-muted-foreground">
          {detail}
        </span>
      )}
    </span>
  );
}

/** "Mon 28 Sep, 14:05" on the event's clock. */
export function formatRequestTime(value: Date, timeZone?: string): string {
  return value.toLocaleString(APP_LOCALE, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: usableTimeZone(timeZone),
  });
}
